import { useEffect, useState } from 'react';

import { api } from '../api/client';
import type { TrialBalance } from '../types';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

export function TrialBalancePage() {
  const [trialBalance, setTrialBalance] = useState<TrialBalance | null>(null);
  const [loading, setLoading] = useState(true);
  const [asOf, setAsOf] = useState('');

  useEffect(() => {
    async function fetchData() {
      try {
        const data = await api.reports.getTrialBalance();
        setTrialBalance(data);
      } catch (error) {
        console.error('Failed to fetch trial balance:', error);
      } finally {
        setLoading(false);
      }
    }
    void fetchData();
  }, []);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <div className="animate-spin text-3xl mb-4"><i className="bi bi-arrow-repeat"></i></div>
        <p className="text-muted-foreground">Loading trial balance...</p>
      </div>
    );
  }

  if (!trialBalance) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <p className="text-muted-foreground">Unable to load trial balance</p>
      </div>
    );
  }

  // Group accounts by type
  const groupedAccounts = trialBalance.accounts.reduce((acc, account) => {
    if (!acc[account.type]) {
      acc[account.type] = [];
    }
    acc[account.type].push(account);
    return acc;
  }, {} as Record<string, typeof trialBalance.accounts>);

  const typeOrder = ['ASSET', 'LIABILITY', 'EQUITY', 'REVENUE', 'EXPENSE'];
  const typeLabels: Record<string, string> = {
    ASSET: 'Assets',
    LIABILITY: 'Liabilities',
    EQUITY: 'Equity',
    REVENUE: 'Revenue',
    EXPENSE: 'Expenses',
  };

  return (
    <div className="flex flex-col gap-6">
      {/* Page Header */}
      <div className="flex justify-between items-center pb-4 border-b border-border">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Trial Balance</h1>
          <p className="text-sm text-muted-foreground">Verify that total debits equal total credits</p>
        </div>
        <div className="flex gap-3">
          <input
            type="date"
            className="px-3 py-2 text-sm border border-input rounded-md bg-background"
            value={asOf}
            onChange={(e) => setAsOf(e.target.value)}
            placeholder="As of date"
          />
          <Button variant="outline" size="sm">Export CSV</Button>
          <Button variant="default" size="sm">Print</Button>
        </div>
      </div>

      {/* Balance Status */}
      <Card className={trialBalance.is_balanced ? 'border-green-500 bg-green-50 dark:bg-green-950/20' : 'border-red-500 bg-red-50 dark:bg-red-950/20'}>
        <CardContent className="pt-6">
          <div className="flex items-center gap-3">
            {trialBalance.is_balanced ? (
              <>
                <i className="bi bi-check-circle-fill text-2xl text-green-600"></i>
                <div>
                  <h3 className="font-semibold text-foreground">Books are balanced</h3>
                  <p className="text-sm text-muted-foreground">Total debits equal total credits</p>
                </div>
              </>
            ) : (
              <>
                <i className="bi bi-exclamation-triangle-fill text-2xl text-red-600"></i>
                <div>
                  <h3 className="font-semibold text-foreground">Books are out of balance</h3>
                  <p className="text-sm text-red-600">Difference: {formatCurrency(trialBalance.difference)}</p>
                </div>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Totals Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total Debits</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold font-mono text-red-600">
              {formatCurrency(trialBalance.total_debits)}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total Credits</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold font-mono text-green-600">
              {formatCurrency(trialBalance.total_credits)}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Difference</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold font-mono">
              {formatCurrency(Math.abs(trialBalance.difference))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Trial Balance by Type */}
      {typeOrder.map((type) => {
        const accounts = groupedAccounts[type] || [];
        if (accounts.length === 0) return null;

        const typeDebits = accounts.reduce((sum, a) => sum + a.debit, 0);
        const typeCredits = accounts.reduce((sum, a) => sum + a.credit, 0);

        return (
          <Card key={type}>
            <CardHeader className="pb-2">
              <div className="flex justify-between items-center">
                <CardTitle>{typeLabels[type]}</CardTitle>
                <div className="text-sm text-muted-foreground">
                  <span className="mr-4">Dr: {formatCurrency(typeDebits)}</span>
                  <span>Cr: {formatCurrency(typeCredits)}</span>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="py-2 px-3 text-left font-medium text-muted-foreground">Code</th>
                      <th className="py-2 px-3 text-left font-medium text-muted-foreground">Account</th>
                      <th className="py-2 px-3 text-right font-medium text-muted-foreground">Debit</th>
                      <th className="py-2 px-3 text-right font-medium text-muted-foreground">Credit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {accounts.map((account) => (
                      <tr key={account.id} className="border-b border-border hover:bg-muted/50">
                        <td className="py-2 px-3"><code className="text-xs bg-muted px-1.5 py-0.5 rounded">{account.code}</code></td>
                        <td className="py-2 px-3">{account.name}</td>
                        <td className="py-2 px-3 text-right font-mono">
                          {account.debit > 0 ? formatCurrency(account.debit) : ''}
                        </td>
                        <td className="py-2 px-3 text-right font-mono">
                          {account.credit > 0 ? formatCurrency(account.credit) : ''}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}